import React, { createContext, useContext, useState, useCallback } from 'react';
import api, { session } from '../ui/api';
import { clearSnapshots } from '../ui/offline/snapshotStore';
import useSounds from '../hooks/useSounds';
import { clear } from '../ui/cache';

const AuthCtx = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};

/**
 * Passwordless session state: name + role -> (disambiguate) -> SMS code
 * -> one 30-day token. No password, no refresh cycle; an expired or
 * revoked token is caught by the API client on the next call.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => session.user());
  const sounds = useSounds();

  const lookup = useCallback((name, role, parentType) => api.loginLookup(name, role, parentType), []);
  const select = useCallback((userId) => api.loginSelect(userId), []);

  const verify = useCallback(async (userId, code) => {
    const data = await api.loginVerify(userId, code, navigator.userAgent);
    // Wipe cached data BEFORE the new session is stored. Cache entries
    // are scoped per user id, but the school shares machines: the office
    // computer is signed in and out of all day. Clearing on the way IN
    // as well as OUT means a stale entry can never outlive the person it
    // belonged to — the API filters by role, so a teacher's cached
    // /students is a different, smaller list than an admin's.
    clear();
    clearSnapshots();
    session.save(data.token, data.user);
    setUser(data.user);
    sounds.login();
    return data.user;
  }, [sounds]);

  const logout = useCallback(async () => {
    sounds.logout();
    try { await api.logout(); } catch { /* the local session goes either way */ }
    session.clear();
    clear();
    clearSnapshots();
    setUser(null);
  }, [sounds]);

  const role = user?.role || null;
  const value = {
    user,
    role,
    isOffice: role === 'admin' || role === 'register',
    isAdmin: role === 'admin',
    isTeacher: role === 'teacher',
    isStudent: role === 'student',
    lookup,
    select,
    verify,
    logout,
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
